import { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import {
  PlusCircle,
  Package,
  DollarSign,
  Tag,
  FileText,
  Pencil,
  Trash2,
  Search,
  Loader2,
  MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import MastersLayout from "@/components/masters/MastersLayout";
import TablePagination from "@/components/masters/TablePagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/contexts/AuthContext";
import {
  serviceApi,
  accountApi,
  BASE_URL,
  authenticatedApi,
  groupApi,
} from "@/services/api";

// Constants
const ITEMS_PER_PAGE = 10;
// Status options for the filter dropdown
const SERVICE_STATUS_OPTIONS = [
  { label: "Active", value: "true" },
  { label: "Inactive", value: "false" },
];

// Service interface
interface Service {
  id: string;
  organizationId: string;
  name: string;
  code: string;
  groupId: string;
  amount: number;
  accountId: string;
  status: boolean;
  createdAt: string;
  updatedAt: string;
  group?: {
    name: string;
  };
  account?: {
    name: string;
  };
}

// Account interface
interface Account {
  id: string;
  name: string;
  code: string;
  type: string;
  visibility: string;
  openingBalance: number;
  balanceType: string;
  status: boolean;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

// Group interface
interface Group {
  id: string;
  name: string;
  description: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

const ServicesPage = () => {
  const { user } = useAuth();
  const [services, setServices] = useState<Service[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteServiceName, setDeleteServiceName] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    code: "",
    name: "",
    groupId: "",
    amount: "",
    accountId: "",
    status: true,
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [filters, setFilters] = useState({
    groupId: "all",
    accountId: "all",
    status: "all",
    search: "",
  });

  const { toast } = useToast();

  // Fetch services from API
  const fetchServices = async () => {
    setLoading(true);
    try {
      const response = await serviceApi.getServices({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        query: filters.search, // Use query parameter for searching name and code
        groupId: filters.groupId !== "all" ? filters.groupId : undefined,
        accountId: filters.accountId !== "all" ? filters.accountId : undefined,
        status: filters.status !== "all" ? filters.status : undefined,
      });

      // The response is already parsed JSON
      setServices(response.docs || []);
      setTotalDocs(response.totalDocs || 0);
      setHasNextPage(response.hasNextPage || false);
      setHasPrevPage(response.hasPreviousPage || false);
    } catch (error) {
      console.error("Error fetching services:", error);
      toast({
        title: "Error",
        description: "Failed to fetch services. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch groups and accounts for the form
  const fetchFormData = async () => {
    if (!user?.organizationId) {
      console.log("No organization ID found, skipping fetchFormData");
      return;
    }

    console.log("Fetching form data for filters...");
    try {
      // Fetch groups from the API
      console.log("Fetching groups...");
      const groupsResponse = await groupApi.getGroups();
      console.log("Groups response:", groupsResponse);
      setGroups(groupsResponse.docs || []);

      // Fetch accounts from the API with status=true
      console.log("Fetching accounts with status=true...");
      const accountsResponse = await accountApi.getAccounts({ status: "true" });
      console.log("Accounts response:", accountsResponse);
      setAccounts(accountsResponse.docs || []);
    } catch (error) {
      console.error("Error fetching form data:", error);
      toast({
        title: "Error",
        description: "Failed to fetch form data. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Load data on initial render and when filters or page changes
  useEffect(() => {
    if (user?.organizationId) {
      fetchServices();
    }
  }, [user?.organizationId, currentPage, filters]);

  // Load form data (groups and accounts) on initial render and when form is shown
  useEffect(() => {
    if (user?.organizationId) {
      fetchFormData();
    }
  }, [user?.organizationId]);

  const handleInputChange = (
    field: string,
    value: string | boolean | number
  ) => {
    // Capitalize first character for service name
    if (field === "name" && typeof value === "string") {
      const capitalizedValue = value.charAt(0).toUpperCase() + value.slice(1);
      setFormData({ ...formData, [field]: capitalizedValue });
    } else {
      // Don't trim during typing, store the value as is
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleEdit = async (service: Service) => {
    setEditingId(service.id);
    setFormLoading(true);

    try {
      const serviceData = await serviceApi.getService(service.id);

      if (serviceData.data) {
        // Format amount to prevent exponential notation
        const formattedAmount =
          serviceData.data.amount <= 99999999.99
            ? serviceData.data.amount.toFixed(2)
            : "0.00";

        setFormData({
          code: serviceData.data.code.trim(),
          name: serviceData.data.name.trim(),
          groupId: serviceData.data.groupId,
          amount: formattedAmount,
          accountId: serviceData.data.accountId,
          status: serviceData.data.status,
        });
        setShowForm(true);
      }
    } catch (error) {
      console.error("Error fetching service details:", error);
      toast({
        title: "Error",
        description:
          error.message ?? "Failed to fetch service details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteClick = (service: Service) => {
    setDeleteId(service.id);
    setDeleteServiceName(service.name);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setLoading(true);
    try {
      await serviceApi.deleteService(deleteId);

      toast({
        title: "Service Deleted",
        description: `${deleteServiceName} has been deleted successfully.`,
      });

      // Refresh the services list
      fetchServices();
    } catch (error) {
      console.error("Error deleting service:", error);
      toast({
        title: "Error",
        description:
          error.message ?? "Failed to delete service. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setShowDeleteDialog(false);
      setDeleteId(null);
      setDeleteServiceName("");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!user?.organizationId) return;

    setFormLoading(true);
    try {
      // Validate and format amount
      let amount = 0;
      if (formData.amount && formData.amount.toString().trim()) {
        const parsedAmount = parseFloat(formData.amount.toString().trim());
        if (isNaN(parsedAmount) || parsedAmount > 99999999.99) {
          toast({
            title: "Invalid Amount",
            description: "Please enter a valid amount (max: 99,999,999.99)",
            variant: "destructive",
          });
          setFormLoading(false);
          return;
        }
        if (parsedAmount < 0) {
          toast({
            title: "Invalid Amount",
            description: "Amount must be greater than or equal to zero.",
            variant: "destructive",
          });
          setFormLoading(false);
          return;
        }
        amount = parsedAmount;
      }

      // Trim string fields before submission and ensure name is capitalized
      const serviceData = {
        ...formData,
        name:
          formData.name.trim().charAt(0).toUpperCase() +
          formData.name.trim().slice(1),
        code: formData.code.trim(),
        amount: amount,
        organizationId: user.organizationId,
      };

      if (editingId) {
        // Update existing service
        await serviceApi.updateService(editingId, serviceData);

        toast({
          title: "Service Updated",
          description: `${serviceData.name} has been updated successfully.`,
        });
      } else {
        // Create new service
        await serviceApi.createService(serviceData);

        toast({
          title: "Service Created",
          description: `${serviceData.name} has been created successfully.`,
        });
      }

      // Reset form and refresh services
      resetFormData();
      setEditingId(null);
      setShowForm(false);
      fetchServices();
    } catch (error) {
      console.error("Error saving service:", error);
      toast({
        title: "Error",
        description:
          error.message ?? "Failed to save service. Please try again.",
        variant: "destructive",
      });
    } finally {
      setFormLoading(false);
    }
  };

  // Helper function to reset form data
  const resetFormData = () => {
    setFormData({
      code: "",
      name: "",
      groupId: "",
      amount: "",
      accountId: "",
      status: true,
    });
  };

  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Filter services based on the filters state
  const filteredServices = services.filter((service) => {
    // Filter by groupId
    const groupMatch =
      filters.groupId === "all" || service.groupId === filters.groupId;

    // Filter by accountId
    const accountMatch =
      filters.accountId === "all" || service.accountId === filters.accountId;

    // Filter by status
    const statusMatch =
      filters.status === "all" ||
      (filters.status === "true" && service.status === true) ||
      (filters.status === "false" && service.status === false);

    // Filter by search term
    const searchMatch =
      !filters.search ||
      service.name.toLowerCase().includes(filters.search.toLowerCase()) ||
      service.code.toLowerCase().includes(filters.search.toLowerCase()) ||
      (service.group?.name?.toLowerCase() || "").includes(
        filters.search.toLowerCase()
      ) ||
      (service.account?.name?.toLowerCase() || "").includes(
        filters.search.toLowerCase()
      );

    return groupMatch && accountMatch && statusMatch && searchMatch;
  });

  // Update pagination calculations to use filteredServices
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentItems = filteredServices.slice(
    indexOfFirstItem,
    indexOfLastItem
  );
  const totalPages = Math.ceil(filteredServices.length / ITEMS_PER_PAGE);

  return (
    <MastersLayout
    // title="Services"
    // description="Manage system services and their configurations"
    >
      <div className="space-y-4">
        {/* Responsive filter section */}
        <div className="space-y-4">
          {/* Search field */}
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search services..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="pl-8"
            />
          </div>

          {/* Filter dropdowns - responsive grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
            <Select
              value={filters.groupId}
              onValueChange={(value) => handleFilterChange("groupId", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Groups" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Groups</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.accountId}
              onValueChange={(value) => handleFilterChange("accountId", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Accounts" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Accounts</SelectItem>
                {accounts.map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={filters.status}
              onValueChange={(value) => handleFilterChange("status", value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                {SERVICE_STATUS_OPTIONS.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              onClick={() => {
                setFilters({
                  groupId: "all",
                  accountId: "all",
                  status: "all",
                  search: "",
                });
                setCurrentPage(1);
              }}
              className="w-full sm:w-auto"
            >
              Reset
            </Button>
          </div>
        </div>

        {/* <Card>
          <CardHeader> */}
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Services</CardTitle>
                <CardDescription>
                  Manage system services and their configurations
                </CardDescription>
              </div>
              <Button
                onClick={() => {
                  resetFormData();
                  setEditingId(null);
                  setShowForm(true);
                }}
              >
                <PlusCircle className="mr-2 h-4 w-4" />
                Add Service
              </Button>
            </div>
          {/* </CardHeader>
          <CardContent> */}
            <div className="overflow-x-auto -mx-4 sm:mx-0">
              <div className="min-w-full inline-block align-middle">
                <div className="overflow-hidden">
                  <Table className="min-w-full divide-y divide-gray-200">
                    <TableHeader>
                      <TableRow>
                        <TableHead className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider">
                          Name
                        </TableHead>
                        <TableHead className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider">
                          Code
                        </TableHead>
                        <TableHead className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider">
                          Group
                        </TableHead>
                        <TableHead className="px-2 py-3 text-left text-xs font-medium uppercase tracking-wider">
                          Amount
                        </TableHead>
                        <TableHead className="hidden md:table-cell px-2 py-3 text-left text-xs font-medium uppercase tracking-wider">
                          Account
                        </TableHead>
                        <TableHead className="whitespace-nowrap px-3 py-2">
                          Status
                        </TableHead>
                        <TableHead className="w-[50px] px-3 py-2">
                          Actions
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {currentItems.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            className="px-2 py-8 text-center text-sm text-gray-500"
                          >
                            {filters.search ||
                            filters.groupId !== "all" ||
                            filters.status !== "all"
                              ? "No services match the selected filters"
                              : "No services found"}
                          </TableCell>
                        </TableRow>
                      ) : (
                        currentItems.map((service) => (
                          <TableRow key={service.id} className="h-10">
                            <TableCell className="px-2 py-2 text-sm font-medium ">
                              {service.name}
                            </TableCell>
                            <TableCell className="px-2 py-2 text-sm ">
                              {service.code}
                            </TableCell>
                            <TableCell className="px-2 py-2 text-sm ">
                              {service.group?.name || ""}
                            </TableCell>
                            <TableCell className="px-2 py-2 text-sm ">
                              {service.amount <= 99999999.99
                                ? service.amount.toFixed(2)
                                : "0.00"}
                            </TableCell>
                            <TableCell className="hidden md:table-cell px-2 py-2 text-sm ">
                              {service.account?.name || ""}
                            </TableCell>
                            <TableCell className="px-2 py-2 text-sm ">
                              <div className="flex items-center gap-2">
                                <Switch
                                  id={`status-${service.id}`}
                                  checked={service.status}
                                  onCheckedChange={async (checked) => {
                                    try {
                                      setLoading(true);
                                      await serviceApi.updateService(
                                        service.id,
                                        {
                                          ...service,
                                          status: checked,
                                        }
                                      );
                                      fetchServices(); // Refresh the list
                                      toast({
                                        title: "Success",
                                        description: `Service status updated to ${
                                          checked ? "Active" : "Inactive"
                                        }`,
                                      });
                                    } catch (error) {
                                      console.error(
                                        "Error updating service status:",
                                        error
                                      );
                                      toast({
                                        title: "Error",
                                        description:
                                          error.message ??
                                          "Failed to update service status",
                                        variant: "destructive",
                                      });
                                    } finally {
                                      setLoading(false);
                                    }
                                  }}
                                />
                                <span className="text-xs">
                                  {service.status ? "Active" : "Inactive"}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="px-3 py-2">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8 p-0">
                                    <MoreHorizontal className="h-4 w-4" />
                                    <span className="sr-only">Open menu</span>
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => handleEdit(service)} className="cursor-pointer">
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={() => handleDeleteClick(service)} 
                                    className="cursor-pointer text-destructive focus:text-destructive"
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>

            {filteredServices.length > ITEMS_PER_PAGE && (
              <TablePagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />
            )}
          {/* </CardContent>
        </Card> */}
      </div>

      {showForm && (
        <Dialog
          open={showForm}
          onOpenChange={(open) => {
            if (!open) {
              resetFormData();
              setEditingId(null);
            }
            setShowForm(open);
          }}
        >
          <DialogContent className="max-w-4xl w-full">
            <DialogHeader>
              <DialogTitle>
                {editingId ? "Edit" : "Add New"} Service
              </DialogTitle>
              <DialogDescription>
                {editingId
                  ? "Update the service information below"
                  : "Fill in the service information below"}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="grid gap-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Service Name</Label>
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      placeholder="Enter service name"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="code">Service Code</Label>
                  <div className="flex items-center gap-2">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <Input
                      id="code"
                      value={formData.code}
                      onChange={(e) =>
                        handleInputChange("code", e.target.value)
                      }
                      placeholder="Enter service code"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="groupId">Group</Label>
                  <Select
                    value={formData.groupId}
                    onValueChange={(value) =>
                      handleInputChange("groupId", value)
                    }
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select group" />
                    </SelectTrigger>
                    <SelectContent>
                      {groups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="amount">Amount</Label>
                  <div className="flex items-center gap-2">
                    {/* <DollarSign className="h-4 w-4 text-muted-foreground" /> */}
                    <Input
                      id="amount"
                      type="text"
                      value={formData.amount}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow valid number format with up to 2 decimal places and max 10 digits total
                        if (value === "" || /^\d{0,8}\.?\d{0,2}$/.test(value)) {
                          // Additional check to prevent numbers larger than 99999999.99
                          if (
                            value === "" ||
                            parseFloat(value) <= 99999999.99
                          ) {
                            handleInputChange("amount", value);
                          }
                        }
                      }}
                      onBlur={(e) => {
                        // Format the number properly on blur
                        const value = e.target.value;
                        if (value && !isNaN(parseFloat(value))) {
                          const num = parseFloat(value);
                          if (num <= 99999999.99) {
                            handleInputChange("amount", num.toFixed(2));
                          }
                        }
                      }}
                      placeholder="e.g., 25.00"
                      defaultValue={"0.0"}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="accountId">Account</Label>
                  <Select
                    value={formData.accountId}
                    onValueChange={(value) =>
                      handleInputChange("accountId", value)
                    }
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select account" />
                    </SelectTrigger>
                    <SelectContent>
                      {accounts.map((account) => (
                        <SelectItem key={account.id} value={account.id}>
                          {account.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="status">Status</Label>
                  <div className="flex items-center gap-2">
                    <Switch
                      id="status"
                      checked={formData.status}
                      onCheckedChange={(checked) =>
                        handleInputChange("status", checked)
                      }
                    />
                    <span>{formData.status ? "Active" : "Inactive"}</span>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetFormData();
                    setEditingId(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit">
                  {editingId ? "Update" : "Create"} Service
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the service "{deleteServiceName}"?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MastersLayout>
  );
};

export default ServicesPage;
